import {
  Calculator,
  IndianRupee,
  Plus,
  Trash2,
  TrendingUp,
} from "lucide-react";

const inputClass =
  "w-full min-w-[130px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const labelClass = "text-sm font-medium text-slate-700";

function createRowId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(toNumber(value));
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "number",
  placeholder = "0",
  suffix,
}) {
  return (
    <label className="block">
      <span className={labelClass}>{label}</span>

      <div className="relative mt-1.5">
        <input
          type={type}
          name={name}
          value={value ?? ""}
          onChange={onChange}
          min={type === "number" ? "0" : undefined}
          step={type === "number" ? "0.01" : undefined}
          placeholder={placeholder}
          className={`${inputClass} ${suffix ? "pr-14" : ""}`}
        />

        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500">
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  const styles = {
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };

  return (
    <div
      className={`rounded-xl border p-5 shadow-sm ${
        styles[color] || styles.blue
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="mt-2 text-2xl font-bold">{value}</p>
        </div>

        <div className="rounded-xl bg-white/70 p-3">
          <Icon size={23} />
        </div>
      </div>
    </div>
  );
}

export default function FinancialEngineeringForm({ data, onChange }) {
  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    onChange({
      ...data,
      [name]: value,
    });
  };

  const getBasisValue = (row) => {
    const projectValue = toNumber(data.projectBaseValue);
    const emdAmount = toNumber(data.emdBaseAmount);
    const pbgPercentage = toNumber(data.pbgPercentage) / 100;
    const upfrontCost = toNumber(data.upfrontCost);

    if (row.parameter.includes("EMD Type")) {
      return emdAmount;
    }

    if (row.parameter.includes("Performance Bank Guarantee")) {
      return projectValue * pbgPercentage;
    }

    if (row.parameter.includes("GeM Portal")) {
      return projectValue;
    }

    if (row.parameter.includes("Payment Terms")) {
      return upfrontCost;
    }

    return toNumber(row.basisValue);
  };

  const getDuration = (row) => {
    if (row.parameter.includes("EMD Type")) {
      return toNumber(data.emdValidityDays);
    }

    if (row.parameter.includes("Performance Bank Guarantee")) {
      return toNumber(data.pbgValidityDays);
    }

    if (row.parameter === "Payment Terms: Quarterly Advance") {
      return toNumber(data.contractDurationYears) * 4;
    }

    if (row.parameter === "Payment Terms: Quarterly Arrear") {
      return toNumber(data.contractDurationYears) * 4 + 1;
    }

    return toNumber(row.duration);
  };

  const calculateQuarterlyFinanceCost = (
    basisValue,
    annualRate,
    numberOfQuarters
  ) => {
    if (
      basisValue <= 0 ||
      annualRate <= 0 ||
      numberOfQuarters <= 0
    ) {
      return 0;
    }

    let cost = 0;
    const quarterlyReduction = basisValue / numberOfQuarters;

    for (let quarter = 0; quarter < numberOfQuarters; quarter += 1) {
      const outstandingAmount =
        basisValue - quarterlyReduction * quarter;

      cost += Math.max(outstandingAmount, 0) * (annualRate / 4);
    }

    return cost;
  };

  const calculateRowCost = (row) => {
    const basisValue = getBasisValue(row);
    const fixedRate = toNumber(row.fixedCostPercentage) / 100;
    const variableRate = toNumber(row.variableCostPercentage) / 100;
    const duration = getDuration(row);

    if (row.parameter === "Payment Terms: Quarterly Advance") {
      return calculateQuarterlyFinanceCost(
        basisValue,
        variableRate,
        duration
      );
    }

    if (row.parameter === "Payment Terms: Quarterly Arrear") {
      return calculateQuarterlyFinanceCost(
        basisValue,
        variableRate,
        duration
      );
    }

    if (row.parameter === "Payment Terms on Arrear") {
      return (
        basisValue * fixedRate +
        basisValue * variableRate * duration
      );
    }

    if (row.parameter.includes("GeM Portal")) {
      return basisValue * fixedRate;
    }

    return (
      basisValue * fixedRate +
      basisValue * variableRate * (duration / 365)
    );
  };

  const updateFinanceRow = (index, field, value) => {
    const updatedRows = data.financeRows.map((row, rowIndex) =>
      rowIndex === index
        ? {
            ...row,
            [field]: value,
          }
        : row
    );

    onChange({
      ...data,
      financeRows: updatedRows,
    });
  };

  const addFinanceRow = () => {
    onChange({
      ...data,
      financeRows: [
        ...data.financeRows,
        {
          id: createRowId(),
          parameter: "",
          basisValue: "",
          fixedCostPercentage: "",
          variableCostPercentage: "",
          duration: "",
          selected: false,
        },
      ],
    });
  };

  const removeFinanceRow = (index) => {
    onChange({
      ...data,
      financeRows: data.financeRows.filter(
        (_, rowIndex) => rowIndex !== index
      ),
    });
  };

  const updateTransactionRow = (index, field, value) => {
    const updatedRows = data.transactionFlow.map((row, rowIndex) =>
      rowIndex === index
        ? {
            ...row,
            [field]: value,
          }
        : row
    );

    onChange({
      ...data,
      transactionFlow: updatedRows,
    });
  };

  const addTransactionRow = () => {
    onChange({
      ...data,
      transactionFlow: [
        ...data.transactionFlow,
        {
          id: createRowId(),
          level: "",
          entity: "",
          flowLogic: "",
          projectedCostFactor: "",
          remarks: "",
        },
      ],
    });
  };

  const removeTransactionRow = (index) => {
    onChange({
      ...data,
      transactionFlow: data.transactionFlow.filter(
        (_, rowIndex) => rowIndex !== index
      ),
    });
  };

  const totalFinanceCost = data.financeRows.reduce((total, row) => {
    if (!row.selected) return total;
    return total + calculateRowCost(row);
  }, 0);

  const selectedCostCount = data.financeRows.filter(
    (row) => row.selected
  ).length;

  const projectValue = toNumber(data.projectBaseValue);
  const financeCostPercentage =
    projectValue > 0 ? (totalFinanceCost / projectValue) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard
          label="Project Base Value"
          value={formatCurrency(data.projectBaseValue)}
          icon={IndianRupee}
          color="blue"
        />

        <SummaryCard
          label="Selected Finance Components"
          value={selectedCostCount}
          icon={Calculator}
          color="amber"
        />

        <SummaryCard
          label="Estimated Cost of Finance"
          value={formatCurrency(totalFinanceCost)}
          icon={TrendingUp}
          color="emerald"
        />
      </div>

      {/* BASE INPUTS */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
          <h3 className="text-base font-semibold text-slate-900">
            Base Input Parameters
          </h3>

          <p className="mt-1 text-sm text-slate-500">
            Enter the tender value, contract duration, EMD and PBG
            requirements.
          </p>
        </div>

        <div className="grid gap-5 p-5 md:grid-cols-2 xl:grid-cols-3">
          <Field
            label="Project Base Value (Excluding GST)"
            name="projectBaseValue"
            value={data.projectBaseValue}
            onChange={handleFieldChange}
            placeholder="Enter project value"
          />

          <Field
            label="Contract Duration"
            name="contractDurationYears"
            value={data.contractDurationYears}
            onChange={handleFieldChange}
            placeholder="Enter duration"
            suffix="Years"
          />

          <Field
            label="EMD Base Amount Required"
            name="emdBaseAmount"
            value={data.emdBaseAmount}
            onChange={handleFieldChange}
            placeholder="Enter EMD amount"
          />

          <Field
            label="EMD Validity"
            name="emdValidityDays"
            value={data.emdValidityDays}
            onChange={handleFieldChange}
            placeholder="Enter validity"
            suffix="Days"
          />

          <Field
            label="PBG Requirement"
            name="pbgPercentage"
            value={data.pbgPercentage}
            onChange={handleFieldChange}
            placeholder="Example: 3"
            suffix="%"
          />

          <Field
            label="PBG Validity"
            name="pbgValidityDays"
            value={data.pbgValidityDays}
            onChange={handleFieldChange}
            placeholder="Enter validity"
            suffix="Days"
          />

          <Field
            label="F1 Upfront Cost Component"
            name="upfrontCost"
            value={data.upfrontCost}
            onChange={handleFieldChange}
            placeholder="Enter upfront cost"
          />
        </div>
      </section>

      {/* FINANCE COST MATRIX */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Cost of Finance Matrix
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Select the applicable finance components. Only selected rows
              contribute to the final cost.
            </p>
          </div>

          <button
            type="button"
            onClick={addFinanceRow}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
          >
            <Plus size={17} />
            Add Finance Parameter
          </button>
        </div>

        <div className="p-5">
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[1500px]">
              <thead className="bg-slate-100">
                <tr>
                  <th className="w-16 px-3 py-3 text-center text-xs font-semibold uppercase text-slate-600">
                    S.No.
                  </th>

                  <th className="min-w-[290px] px-3 py-3 text-left text-xs font-semibold uppercase text-slate-600">
                    Finance Cost Parameter
                  </th>

                  <th className="min-w-[170px] px-3 py-3 text-right text-xs font-semibold uppercase text-slate-600">
                    Basis Value
                  </th>

                  <th className="min-w-[145px] px-3 py-3 text-left text-xs font-semibold uppercase text-slate-600">
                    Fixed Cost %
                  </th>

                  <th className="min-w-[160px] px-3 py-3 text-left text-xs font-semibold uppercase text-slate-600">
                    Variable Cost % p.a.
                  </th>

                  <th className="min-w-[150px] px-3 py-3 text-left text-xs font-semibold uppercase text-slate-600">
                    Days / Quarters
                  </th>

                  <th className="min-w-[190px] px-3 py-3 text-right text-xs font-semibold uppercase text-slate-600">
                    Calculated Cost
                  </th>

                  <th className="min-w-[100px] px-3 py-3 text-center text-xs font-semibold uppercase text-slate-600">
                    Include
                  </th>

                  <th className="min-w-[190px] px-3 py-3 text-right text-xs font-semibold uppercase text-slate-600">
                    Active Cost
                  </th>

                  <th className="w-20 px-3 py-3 text-center text-xs font-semibold uppercase text-slate-600">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 bg-white">
                {data.financeRows.map((row, index) => {
                  const basisValue = getBasisValue(row);
                  const duration = getDuration(row);
                  const calculatedCost = calculateRowCost(row);
                  const activeCost = row.selected ? calculatedCost : 0;

                  const automaticBasis =
                    row.parameter.includes("EMD Type") ||
                    row.parameter.includes(
                      "Performance Bank Guarantee"
                    ) ||
                    row.parameter.includes("GeM Portal") ||
                    row.parameter.includes("Payment Terms");

                  return (
                    <tr key={row.id || index} className="align-top">
                      <td className="px-3 py-3 text-center text-sm font-semibold text-slate-600">
                        {index + 1}
                      </td>

                      <td className="px-3 py-3">
                        <input
                          type="text"
                          value={row.parameter}
                          onChange={(event) =>
                            updateFinanceRow(
                              index,
                              "parameter",
                              event.target.value
                            )
                          }
                          placeholder="Enter finance parameter"
                          className={`${inputClass} min-w-[270px]`}
                        />
                      </td>

                      <td className="px-3 py-3">
                        {automaticBasis ? (
                          <div className="rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-right text-sm font-semibold text-slate-700">
                            {formatCurrency(basisValue)}
                          </div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.basisValue || ""}
                            onChange={(event) =>
                              updateFinanceRow(
                                index,
                                "basisValue",
                                event.target.value
                              )
                            }
                            placeholder="0"
                            className={`${inputClass} text-right`}
                          />
                        )}
                      </td>

                      <td className="px-3 py-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.fixedCostPercentage}
                          onChange={(event) =>
                            updateFinanceRow(
                              index,
                              "fixedCostPercentage",
                              event.target.value
                            )
                          }
                          className={inputClass}
                          placeholder="0"
                        />
                      </td>

                      <td className="px-3 py-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.variableCostPercentage}
                          onChange={(event) =>
                            updateFinanceRow(
                              index,
                              "variableCostPercentage",
                              event.target.value
                            )
                          }
                          className={inputClass}
                          placeholder="0"
                        />
                      </td>

                      <td className="px-3 py-3">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={duration}
                          onChange={(event) =>
                            updateFinanceRow(
                              index,
                              "duration",
                              event.target.value
                            )
                          }
                          disabled={
                            row.parameter.includes("EMD Type") ||
                            row.parameter.includes(
                              "Performance Bank Guarantee"
                            ) ||
                            row.parameter ===
                              "Payment Terms: Quarterly Advance" ||
                            row.parameter ===
                              "Payment Terms: Quarterly Arrear"
                          }
                          className={`${inputClass} ${
                            row.parameter.includes("EMD Type") ||
                            row.parameter.includes(
                              "Performance Bank Guarantee"
                            ) ||
                            row.parameter ===
                              "Payment Terms: Quarterly Advance" ||
                            row.parameter ===
                              "Payment Terms: Quarterly Arrear"
                              ? "cursor-not-allowed bg-slate-100"
                              : ""
                          }`}
                        />
                      </td>

                      <td className="px-3 py-3">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-right text-sm font-semibold text-slate-700">
                          {formatCurrency(calculatedCost)}
                        </div>
                      </td>

                      <td className="px-3 py-3 text-center">
                        <label className="inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            checked={Boolean(row.selected)}
                            onChange={(event) =>
                              updateFinanceRow(
                                index,
                                "selected",
                                event.target.checked
                              )
                            }
                            className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                        </label>
                      </td>

                      <td className="px-3 py-3">
                        <div
                          className={`rounded-lg border px-3 py-2 text-right text-sm font-bold ${
                            row.selected
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-slate-50 text-slate-400"
                          }`}
                        >
                          {formatCurrency(activeCost)}
                        </div>
                      </td>

                      <td className="px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => removeFinanceRow(index)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              <tfoot className="border-t-2 border-slate-300 bg-slate-50">
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-4 text-right text-sm font-bold text-slate-800"
                  >
                    Total Estimated Cost of Finance
                  </td>

                  <td className="px-3 py-4 text-right text-base font-bold text-emerald-700">
                    {formatCurrency(totalFinanceCost)}
                  </td>

                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-800">
              Finance cost represents approximately{" "}
              <strong>{financeCostPercentage.toFixed(2)}%</strong> of the
              project base value.
            </p>
          </div>
        </div>
      </section>

      {/* TRANSACTION FLOW */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Transaction Flow Model
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Define the payment and procurement flow between all entities.
            </p>
          </div>

          <button
            type="button"
            onClick={addTransactionRow}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
          >
            <Plus size={17} />
            Add Flow Level
          </button>
        </div>

        <div className="p-5">
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-slate-100">
                <tr>
                  <th className="min-w-[130px] px-3 py-3 text-left text-xs font-semibold uppercase text-slate-600">
                    Layer Level
                  </th>

                  <th className="min-w-[260px] px-3 py-3 text-left text-xs font-semibold uppercase text-slate-600">
                    Entity Description
                  </th>

                  <th className="min-w-[230px] px-3 py-3 text-left text-xs font-semibold uppercase text-slate-600">
                    Inflow / Outflow Logic
                  </th>

                  <th className="min-w-[190px] px-3 py-3 text-left text-xs font-semibold uppercase text-slate-600">
                    Projected Cost Factor
                  </th>

                  <th className="min-w-[280px] px-3 py-3 text-left text-xs font-semibold uppercase text-slate-600">
                    Remarks
                  </th>

                  <th className="w-20 px-3 py-3 text-center text-xs font-semibold uppercase text-slate-600">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 bg-white">
                {data.transactionFlow.map((row, index) => (
                  <tr key={row.id || index} className="align-top">
                    {[
                      ["level", "Example: Level 1"],
                      ["entity", "Enter entity"],
                      ["flowLogic", "Enter payment logic"],
                      ["projectedCostFactor", "Enter cost factor"],
                      ["remarks", "Enter remarks"],
                    ].map(([field, placeholder]) => (
                      <td key={field} className="px-3 py-3">
                        <input
                          type="text"
                          value={row[field]}
                          onChange={(event) =>
                            updateTransactionRow(
                              index,
                              field,
                              event.target.value
                            )
                          }
                          placeholder={placeholder}
                          className={inputClass}
                        />
                      </td>
                    ))}

                    <td className="px-3 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => removeTransactionRow(index)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}